import { useEffect, useState } from 'react';
import { Button, Result, message } from 'antd';
import { getToken } from '@/utils/auth';

const API = 'https://api.chpen.com/api/auth/v1/';

type Item = { company_id: number; company_name: string | null; is_primary: number };

/**
 * 780CY-54K-99 · 无权限（403）兜底页
 *
 * 场景：切到「自然人」个人身份后，后台菜单全空、路由 access 全部拒绝。
 *   切换器 CompanySwitcher 其实一直在顶栏右侧，但 403 页面本身不说原因、不给出口，
 *   用户看不懂，只能靠后端改缓存捞人（2026-10-31 老陈本人踩过）。
 * 做法：这里把原因写清楚，并给一个「切回主身份」按钮 —— 切换后 reload 整体重取权限与菜单。
 * ⛔ 只做提示与跳转，不放大任何权限；切不成就原样留在这里。
 */
export default function AccessDeniedHint() {
  const [busy, setBusy] = useState(false);
  const [primary, setPrimary] = useState<Item | null>(null);

  const pick = (list: any[]): Item | null => {
    const arr = Array.isArray(list) ? list : [];
    const ok = arr.filter((x) => Number(x.company_id) > 0);
    return ok.find((x) => Number(x.is_primary) === 1) || ok[0] || null;
  };

  const load = async (): Promise<Item | null> => {
    const token = getToken();
    if (!token) return null;
    const r = await fetch(API + 'company-list', {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
    });
    const j = await r.json();
    return pick(j && j.data ? j.data.list : []);
  };

  useEffect(() => {
    (async () => {
      try { setPrimary(await load()); } catch { /* 拿不到就不给按钮，只留说明 */ }
    })();
  }, []);

  const switchBack = async () => {
    setBusy(true);
    try {
      const token = getToken();
      const p = primary || (await load());
      if (!p) { message.warning('没有可切换的企业身份'); setBusy(false); return; }
      const r = await fetch(API + 'switch-company', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + (token || ''),
        },
        body: JSON.stringify({ company_id: Number(p.company_id) }),
      });
      const j = await r.json();
      if (j && (j.code === 0 || j.code === 200)) {
        window.location.reload(); // 整体重取权限与菜单，保证「看到的」=「后端判的」
        return;
      }
      message.warning((j && j.msg) || '切换失败');
    } catch {
      message.warning('切换失败，请重试');
    }
    setBusy(false);
  };

  return (
    <Result
      status="403"
      title="403"
      subTitle={
        primary
          ? '当前是「自然人」个人身份，没有后台管理菜单。切回企业身份即可恢复。'
          : '当前身份没有后台管理菜单权限。'
      }
      extra={
        <Button type="primary" loading={busy} onClick={switchBack}>
          {primary ? '切回主身份：' + (primary.company_name || '企业#' + primary.company_id) : '重新加载'}
        </Button>
      }
    />
  );
}
